import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import {
  authHeaders,
  extractRestaurantId,
  extractRestaurantName,
} from '../restaurant/restaurantApi';
import { bootstrapPickupHubsFromRestaurants, fetchPickupHubs } from './pickupPricingApi';

export { bootstrapPickupHubsFromRestaurants, fetchPickupHubs };

export async function createPickupHub(payload) {
  const res = await axiosRestaurantInstance.post(RESTAURANT_API_PATHS.pickupHubs, payload, {
    headers: authHeaders(),
  });
  return res?.data || null;
}

/**
 * ACTIVE restaurants that do not already have a pickup_hub_mt row.
 * Dedupes GET /restaurant join rows by restaurant id.
 */
export async function fetchRestaurantsAvailableAsHubs(existingHubs = []) {
  const hubRestaurantIds = new Set(
    (Array.isArray(existingHubs) ? existingHubs : [])
      .map((h) => Number(h?.restaurant_id ?? h?.restaurantId))
      .filter((id) => Number.isFinite(id)),
  );

  const res = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurants, {
    headers: authHeaders(),
  });
  const rows = Array.isArray(res?.data) ? res.data : res?.data?.restaurants || [];
  const byId = new Map();

  for (const row of rows) {
    const id = Number(extractRestaurantId(row));
    if (!Number.isFinite(id) || hubRestaurantIds.has(id)) continue;
    const status = String(row?.status ?? row?.Status ?? '').trim().toUpperCase();
    if (status && status !== 'ACTIVE') continue;
    if (byId.has(id)) continue;
    byId.set(id, {
      restaurant_id: id,
      restaurant_name: extractRestaurantName(row) || `Restaurant ${id}`,
      status: status || 'ACTIVE',
    });
  }

  return Array.from(byId.values()).sort((a, b) =>
    String(a.restaurant_name).localeCompare(String(b.restaurant_name)),
  );
}

export async function updatePickupHub(hubId, payload) {
  const res = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.pickupHubById(hubId),
    payload,
    { headers: authHeaders() },
  );
  return res?.data || null;
}

export async function fetchHubTransfers(hubId, direction = 'out') {
  const res = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.pickupHubTransfers(hubId), {
    headers: authHeaders(),
    params: { direction },
  });
  const data = res?.data || {};
  return Array.isArray(data.transfers) ? data.transfers : [];
}

export async function saveHubTransfers(hubId, toHubIds) {
  const res = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.pickupHubTransfers(hubId),
    { to_hub_ids: toHubIds },
    { headers: authHeaders() },
  );
  return res?.data || null;
}

export async function fetchHubSchools(hubId) {
  const res = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.pickupHubSchools(hubId), {
    headers: authHeaders(),
  });
  const data = res?.data || {};
  return Array.isArray(data.schools) ? data.schools : [];
}

export async function mapSchoolToHub(hubId, payload) {
  const res = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.pickupHubSchools(hubId),
    payload,
    { headers: authHeaders() },
  );
  return res?.data || null;
}

export async function unmapSchoolFromHub(hubId, organizationId) {
  const res = await axiosRestaurantInstance.delete(
    RESTAURANT_API_PATHS.pickupHubSchoolByOrg(hubId, organizationId),
    { headers: authHeaders() },
  );
  return res?.data || null;
}
